import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Mobile Onboarding Dialog
 *
 * Full-screen onboarding flow for mobile users on the landing page.
 * Uses the shared MobileOnboarding component with PendingSessionService
 * for pre-auth data collection.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { IndustryContext, JobType, JobLocationType, ShiftType } from '@common/clientServerApi';
import { PendingSessionService } from '../services/pendingSessionService';
import { MobileOnboarding } from '../../components/onboarding';
import { DEFAULT_JOB_TEMPLATES } from '../../../apps/scoring/utils/jobTemplates';
import { DEFAULT_CANDIDATE_TEMPLATES } from '../../../apps/scoring/utils/candidateTemplates';
// ============================================================================
// Main Component
// ============================================================================
export const MobileOnboardingDialog = ({ open, onClose, industry = IndustryContext.Logistics, }) => {
    // File upload state (sample batch is fixed — all templates always shown)
    const [pendingFiles, setPendingFiles] = useState([]);
    // Start fresh session when dialog opens
    React.useEffect(() => {
        if (open) {
            // Start a completely fresh session to avoid old resumes persisting
            const service = new PendingSessionService();
            service.startFreshSession();
            setPendingFiles([]); // Clear any local file state too
        }
    }, [open]);
    // Get all templates
    const templates = useMemo(() => {
        return DEFAULT_JOB_TEMPLATES;
    }, []);
    // File handlers
    const handleAddFiles = useCallback((files) => {
        const first = files[0];
        if (!first)
            return;
        setPendingFiles([{
                file: first,
                id: `${first.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            }]);
    }, []);
    const handleRemoveFile = useCallback((fileId) => {
        setPendingFiles(prev => prev.filter(f => f.id !== fileId));
    }, []);
    const candidateTemplatesForUI = useMemo(() => DEFAULT_CANDIDATE_TEMPLATES.map(c => ({
        id: c.id,
        label: c.label,
        icon: c.icon,
        tagline: c.tagline,
        previewByJobTemplateId: c.previewByJobTemplateId,
        defaultPreview: c.defaultPreview,
    })), []);
    /**
     * Attach either the uploaded files (if user chose the upload path) or the full
     * sample batch (if user came via "Score all N samples"). The two paths are
     * mutually exclusive — the UI never mixes them.
     */
    const attachCandidatesToSession = useCallback((service) => {
        if (pendingFiles.length > 0) {
            service.addFiles(pendingFiles.map(pf => pf.file));
        }
        else {
            DEFAULT_CANDIDATE_TEMPLATES.forEach(tpl => service.addCandidate(tpl.data));
        }
    }, [pendingFiles]);
    // Handle template confirmation - stores in pending session and navigates to recruiter app
    const handleConfirmTemplate = useCallback(async (template, descriptionText) => {
        const service = new PendingSessionService();
        service.getSessionId();
        attachCandidatesToSession(service);
        service.setJobDescription({
            title: template.title,
            descriptionText: descriptionText,
            industry: template.industry,
            jobType: template.jobType,
            locationType: template.locationType,
            shiftType: template.shiftType,
            location: template.location,
            minExperienceYears: 0,
            requirements: template.requirements,
            responsibilities: template.responsibilities,
        });
        const result = await service.syncToBackend();
        if (!result.success) {
            console.error('[MobileOnboarding] Sync errors:', result.errors);
            alert(`Error uploading files: ${result.errors.join(', ')}`);
            return;
        }
        window.location.href = service.getTargetAppUrl();
    }, [attachCandidatesToSession]);
    // Handle custom job creation - stores in pending session and navigates to recruiter app
    const handleCreateCustomJob = useCallback(async (data) => {
        const service = new PendingSessionService();
        attachCandidatesToSession(service);
        service.setJobDescription({
            title: data.title,
            descriptionText: data.descriptionText,
            industry: data.industry || IndustryContext.Logistics,
            jobType: data.jobType || JobType.FullTime,
            locationType: data.locationType || JobLocationType.InPerson,
            shiftType: data.shiftType || ShiftType.Day,
            location: '',
            minExperienceYears: 0,
        });
        const result = await service.syncToBackend();
        if (!result.success) {
            console.error('[MobileOnboarding] Sync errors:', result.errors);
            alert(`Error uploading files: ${result.errors.join(', ')}`);
            return;
        }
        window.location.href = service.getTargetAppUrl();
    }, [attachCandidatesToSession]);
    return (_jsx(MobileOnboarding, { industry: industry, lockedIndustry: true, templates: templates, onConfirmTemplate: handleConfirmTemplate, onCreateCustomJob: handleCreateCustomJob, onClose: onClose, asDialog: true, open: open, pendingFiles: pendingFiles, onAddFiles: handleAddFiles, onRemoveFile: handleRemoveFile, candidateTemplates: candidateTemplatesForUI }));
};
